import { supabase } from './supabaseClient';

/**
 * KPI Service - Fetch and manage KPI data
 * Phase 1: Core Tracking
 */

/**
 * Fetch all users with their KPI metrics
 * Returns: Array of users with performance data
 */
export async function fetchTeamKPIs(roleFilter = 'all') {
  if (!supabase) {
    console.warn('Supabase not configured');
    return [];
  }

  try {
    // Build query based on role filter
    let query = supabase
      .from('users')
      .select(`
        id,
        name,
        role,
        created_at,
        last_login,
        last_active,
        is_online
      `);

    // Filter by role if specified
    if (roleFilter === 'marketer') {
      query = query.eq('role', 'marketer');
    } else if (roleFilter === 'rep') {
      query = query.eq('role', 'rep');
    } else if (roleFilter !== 'all') {
      // Exclude admin and sales_manager from employee view
      query = query.in('role', ['marketer', 'data_entry', 'accountant', 'rep']);
    }

    const { data: users, error: usersError } = await query.order('name');

    if (usersError) throw usersError;
    if (!users || users.length === 0) return [];

    // Fetch KPI metrics for each user
    const userKPIs = await Promise.all(
      users.map(async (user) => {
        const metrics = await getUserMetrics(user.id);
        return {
          ...user,
          ...metrics,
        };
      })
    );

    // Sort by performance score (highest first)
    return userKPIs.sort((a, b) => (b.performanceScore || 0) - (a.performanceScore || 0));
  } catch (err) {
    console.error('❌ Error fetching team KPIs:', err);
    throw err;
  }
}

/**
 * Get detailed metrics for a specific user
 */
async function getUserMetrics(userId) {
  try {
    // Fetch restaurants stats
    const { data: restaurants, error: restError } = await supabase
      .from('restaurants')
      .select('id, workflow_status, created_at, assigned_to, assigned_rep')
      .or(`created_by.eq.${userId},assigned_to.eq.${userId},assigned_rep.eq.${userId}`);

    if (restError) throw restError;

    // Calculate metrics
    const totalRestaurants = (restaurants || []).length;
    const restaurantsAdded = (restaurants || []).filter(r => r.created_by === userId).length;
    const dealsClosed = (restaurants || []).filter(r => r.workflow_status === 'deal_closed').length;
    
    // Calculate conversion rate
    const conversionRate = restaurantsAdded > 0 
      ? ((dealsClosed / restaurantsAdded) * 100).toFixed(1)
      : 0;

    // Fetch daily activity if exists
    const { data: activity, error: activityError } = await supabase
      .from('kpi_daily_activity')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(7);

    if (activityError) {
      console.warn('KPI activity table not ready yet:', activityError.message);
    }

    // Calculate performance score (weighted)
    const performanceScore = calculatePerformanceScore({
      restaurantsAdded,
      dealsClosed,
      activeDays: activity?.length || 0,
    });

    // Calculate today's activity
    const today = new Date().toISOString().split('T')[0];
    const todayActivity = activity?.find(a => a.date === today);

    return {
      totalRestaurants,
      restaurantsAdded,
      dealsClosed,
      conversionRate: parseFloat(conversionRate),
      performanceScore,
      activeDays: activity?.length || 0,
      todayActivity: todayActivity || null,
      weeklyActivity: activity || [],
    };
  } catch (err) {
    console.error(`❌ Error fetching metrics for user ${userId}:`, err);
    return {
      totalRestaurants: 0,
      restaurantsAdded: 0,
      dealsClosed: 0,
      conversionRate: 0,
      performanceScore: 0,
      activeDays: 0,
      todayActivity: null,
      weeklyActivity: [],
    };
  }
}

/**
 * Calculate weighted performance score
 * Formula: (Restaurants × 10) + (Deals × 50) + (Active Days × 5)
 */
function calculatePerformanceScore(metrics) {
  const weights = {
    restaurants: 10,  // points per restaurant added
    deals: 50,        // points per deal closed
    activeDays: 5,    // points per active day
  };

  return (
    (metrics.restaurantsAdded || 0) * weights.restaurants +
    (metrics.dealsClosed || 0) * weights.deals +
    (metrics.activeDays || 0) * weights.activeDays
  );
}

/**
 * Fetch leaderboard for a specific period
 */
export async function fetchLeaderboard(periodType = 'weekly') {
  if (!supabase) return [];

  try {
    // Try to fetch from leaderboard view first
    const { data, error } = await supabase
      .from('v_kpi_current_leaderboard')
      .select('*')
      .eq('period_type', periodType)
      .order('rank_position');

    if (error) {
      console.warn('Leaderboard view not ready, falling back to manual calculation');
      // Fallback: calculate from users
      return await fetchTeamKPIs('all');
    }

    return data || [];
  } catch (err) {
    console.error('❌ Error fetching leaderboard:', err);
    return [];
  }
}

/**
 * Fetch user's daily activity for a date range
 */
export async function fetchUserActivity(userId, startDate, endDate) {
  if (!supabase) return [];

  try {
    const { data, error } = await supabase
      .from('kpi_daily_activity')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('❌ Error fetching user activity:', err);
    return [];
  }
}

/**
 * Set or update user targets
 */
export async function setUserTargets(userId, targets) {
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('kpi_targets')
      .upsert({
        user_id: userId,
        target_type: targets.type || 'daily',
        start_date: targets.startDate || new Date().toISOString().split('T')[0],
        end_date: targets.endDate || new Date().toISOString().split('T')[0],
        restaurants_target: targets.restaurants || 0,
        calls_target: targets.calls || 0,
        visits_target: targets.visits || 0,
        deals_target: targets.deals || 0,
        revenue_target: targets.revenue || 0,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('❌ Error setting user targets:', err);
    throw err;
  }
}

/**
 * Get team summary statistics
 */
export async function getTeamSummary(roleFilter = 'all') {
  try {
    const teamMembers = await fetchTeamKPIs(roleFilter);

    const summary = {
      totalMembers: teamMembers.length,
      totalRestaurants: teamMembers.reduce((sum, u) => sum + (u.restaurantsAdded || 0), 0),
      totalDeals: teamMembers.reduce((sum, u) => sum + (u.dealsClosed || 0), 0),
      avgConversionRate: 0,
      avgPerformanceScore: 0,
      topPerformer: teamMembers[0] || null,
    };

    // Calculate averages
    if (teamMembers.length > 0) {
      summary.avgConversionRate = (
        teamMembers.reduce((sum, u) => sum + (u.conversionRate || 0), 0) / teamMembers.length
      ).toFixed(1);
      
      summary.avgPerformanceScore = Math.round(
        teamMembers.reduce((sum, u) => sum + (u.performanceScore || 0), 0) / teamMembers.length
      );
    }

    return summary;
  } catch (err) {
    console.error('❌ Error getting team summary:', err);
    return {
      totalMembers: 0,
      totalRestaurants: 0,
      totalDeals: 0,
      avgConversionRate: 0,
      avgPerformanceScore: 0,
      topPerformer: null,
    };
  }
}
